import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type { LogicalAlignment } from '@wix/editor-elements-common-utils';
import {
  castFromInheritIfNeeded,
  convertPhysicalInputAlignmentToLogical,
  convertPhysicalInputAlignmentToDirection,
  getHeightInPixels,
  getInputHeight,
  withFallbackValues,
  withSiteDirection,
  resolveInheritanceChain,
} from '@wix/editor-elements-common-utils';
import type {
  TimePickerDefinition,
  ITimePickerMapperProps,
  TimePickerCSSVars,
  TimePickerCssCarmiData,
} from '../TimePicker.types';
import { getEnabledTimes } from '../utils';
import { TranslationKeys } from '../constants';
import type { DatePickerCSSInputPaddingVars } from '../../DatePicker/DatePicker.types';
import { reverseAlignmentOnRtl } from './utils/reverseAlignmentOnRtl';

export const props = withCompInfo<
  ITimePickerMapperProps,
  TimePickerDefinition
>()(
  ['compData', 'compProps', 'hasResponsiveLayout', 'translate'],
  ({ compData, compProps, hasResponsiveLayout, translate }) => {
    const { value, placeholder, step, label } = compData;
    const {
      useAmPmFormat,
      controller,
      initialTime,
      readOnly,
      isDisabled,
      required,
      disabledTimes,
      enabledTimes,
    } = compProps;

    return {
      value,
      placeholder: initialTime === 'none' ? '' : placeholder,
      step,
      label,
      useAmPmFormat,
      controller,
      initialTime,
      readOnly,
      isDisabled,
      required,
      isResponsive: hasResponsiveLayout,
      enabledTimes: getEnabledTimes({ enabledTimes, disabledTimes }),
      translations: {
        invalidTime: translate(
          TranslationKeys.Namespace,
          TranslationKeys.TIME_PICKER_INVALID_TIME,
        ),
        ariaLabel:
          translate(
            TranslationKeys.ARIA_LABEL_NAMESPACE,
            TranslationKeys.TIME_PICKER_INPUT_ARIA_LABELS,
          ) || TranslationKeys.TIME_PICKER_INPUT_ARIA_LABELS_DEFAULT,
      },
    };
  },
);

export const css = withCompInfo<
  TimePickerCSSVars,
  TimePickerDefinition,
  TimePickerCssCarmiData,
  {
    styleProperties: {
      align: LogicalAlignment;
      inputAlign: LogicalAlignment;
      labelAlign: LogicalAlignment;
    };
  }
>()(
  [
    'compData',
    'compLayout',
    'compSingleLayout',
    'styleProperties',
    'compProps',
    'isMobileView',
    'hasResponsiveLayout',
    'isOneDocMigrated',
    'directionWithOptInRtlSupport',
  ],
  (
    {
      compData,
      compLayout,
      compSingleLayout,
      styleProperties,
      compProps,
      isMobileView,
      hasResponsiveLayout,
      isOneDocMigrated,
      directionWithOptInRtlSupport,
    },
    cssVars,
  ) => {
    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      inputDirection = 'inherit',
    } = compData;
    const { labelMargin, labelPadding, margin } = compProps;
    const { align, inputAlign, labelAlign } = styleProperties;

    const calculatedLabelAlign = castFromInheritIfNeeded(labelAlign, align);
    const calculatedInputDirection = resolveInheritanceChain(
      [inputDirection, direction],
      directionWithOptInRtlSupport,
    );

    // Input direction doesn't actually change the direction in css but the flex-direction,
    // therefore we need to reverse logical alignment on rtl
    const calculatedInputAlign = reverseAlignmentOnRtl(
      castFromInheritIfNeeded(inputAlign, align),
      calculatedInputDirection,
    );

    const alignmentCssVars = () => {
      const map: Record<
        string,
        Pick<TimePickerCSSVars, '--arrowMargin' | '--flexDirection'>
      > = {
        ltr: {
          '--arrowMargin': '0 20px 0 0',
          '--flexDirection': 'row',
        },
        rtl: {
          '--arrowMargin': '0 0 0 20px',
          '--flexDirection': 'row-reverse',
        },
      };

      return map[calculatedInputDirection];
    };

    const paddingVariables = (
      fieldName: 'input' | 'label',
      calculatedFieldAlign: LogicalAlignment,
      padding: number,
    ): DatePickerCSSInputPaddingVars => {
      if (isMobileView && fieldName === 'input') {
        return {
          [`--${fieldName}Padding_start`]: `${padding}px`,
          [`--${fieldName}Padding_end`]: `${padding}px`,
        };
      }
      if (calculatedFieldAlign === 'center') {
        return {
          [`--${fieldName}Padding_start`]: `0px`,
          [`--${fieldName}Padding_end`]: `0px`,
        };
      }

      return {
        [`--${fieldName}Padding_${calculatedFieldAlign}`]: `${padding}px`,
      };
    };

    const directionalityProps = {
      '--labelDirection': labelDirection,
      '--align': align,
      '--inputAlign': calculatedInputAlign,
      '--labelAlign': calculatedLabelAlign,
      ...paddingVariables('input', calculatedInputAlign, margin),
      ...paddingVariables('label', calculatedLabelAlign, labelPadding),
      ...alignmentCssVars(),
      '--dir': withSiteDirection(direction),
    };

    const sharedProps = {
      ...cssVars,
      '--labelMarginBottom': `${labelMargin}px`,
      ...directionalityProps,
    };

    if (hasResponsiveLayout) {
      return sharedProps;
    } else {
      const height = isOneDocMigrated
        ? getHeightInPixels(compSingleLayout)
        : compLayout.height;

      return {
        ...sharedProps,
        ...(!isOneDocMigrated && {
          height: 'auto',
        }),
        '--inputHeight': getInputHeight({
          inputHeightProps: compProps,
          height,
          isMobileView: false,
        }),
      };
    }
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'alignment', target: 'direction' }],
        enhancer: convertPhysicalInputAlignmentToDirection,
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'align' }],
        enhancer: convertPhysicalInputAlignmentToLogical,
      },
    ]),
  ],
);

export default createComponentMapperModel({ css, props });
